/** Store → Products list: search/filters, bulk status/delete, CSV import/export. */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Dropdown, Empty, Input, Modal, Select, Space, Table, Typography, Upload, message } from 'antd';
import { DownloadOutlined, DownOutlined, EditOutlined, PlusOutlined, UploadOutlined, InboxOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { directUpload } from './api';
import { StatusTag, listOf, money, sitePath, toastErr, totalOf, useSite } from './common';
import { Thumb, useCollections } from './ShopCommon';
import TranslateCatalogButton, { TranslateCatalogBanner } from './TranslateCatalog';

const { Text } = Typography;

const ImportModal = ({ open, onClose, onDone }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [report, setReport] = useState(null);
    useEffect(() => { if (open) { setFile(null); setReport(null); } }, [open]);
    const run = async () => {
        if (!file) return;
        setBusy(true);
        try {
            const { key } = await directUpload(api, file, 'import');
            const r = await api.post('/products/import', { key });
            setReport(r || {});
            onDone?.();
        } catch (e) { toastErr(e, t('Import failed')); } finally { setBusy(false); }
    };
    const errors = listOf(report?.errors);
    return (
        <Modal open={open} onCancel={onClose} title={t('Import products')} destroyOnHidden
            footer={report ? <Button type="primary" onClick={onClose}>{t('Done')}</Button> : undefined}
            onOk={run} okText={t('Import')} confirmLoading={busy} okButtonProps={{ disabled: !file }}>
            {report ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Alert type={errors.length ? 'warning' : 'success'} showIcon
                        message={t('Created {{c}}, updated {{u}}', { c: report.created ?? 0, u: report.updated ?? 0 })} />
                    {errors.length > 0 && (
                        <div style={{ maxHeight: 240, overflow: 'auto' }}>
                            {errors.map((e, i) => <div key={i}><Text type="danger">{typeof e === 'string' ? e : `${e.row ? `#${e.row}: ` : ''}${e.message || e.error || JSON.stringify(e)}`}</Text></div>)}
                        </div>
                    )}
                </Space>
            ) : (
                <Upload.Dragger accept=".csv,text/csv" maxCount={1} beforeUpload={(f) => { setFile(f); return false; }}
                    onRemove={() => setFile(null)} fileList={file ? [{ uid: '1', name: file.name, status: 'done' }] : []}>
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p>{t('Drop a CSV file here or click to choose')}</p>
                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Shopify product CSV format is supported')}</Text>
                </Upload.Dragger>
            )}
        </Modal>
    );
};

const Products = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { api, siteId, currency } = useSite();
    const collections = useCollections();
    const [filters, setFilters] = useState({ q: '', status: undefined, collection_id: undefined, page: 1, per_page: 25 });
    const [q, setQ] = useState('');
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [sel, setSel] = useState([]);
    const [importing, setImporting] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const d = await api.get('/products', Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== '')));
            setData({ items: listOf(d), total: totalOf(d) });
        } catch (e) {
            if (e.status !== 404) toastErr(e);
            setData({ items: [], total: 0 });
        } finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [filters, api]);

    const vendors = useMemo(() => [...new Set(data.items.map((p) => p.vendor).filter(Boolean))], [data.items]);
    const types = useMemo(() => [...new Set(data.items.map((p) => p.product_type).filter(Boolean))], [data.items]);

    const bulk = async (fn, done) => {
        setBusy(true);
        let failed = 0;
        for (const id of sel) {
            try { await fn(id); } catch (_) { failed += 1; }
        }
        setBusy(false);
        if (failed) message.warning(t('{{n}} failed', { n: failed })); else message.success(done);
        setSel([]);
        load();
    };
    const setStatus = (status) => bulk((id) => api.put(`/products/${id}`, { status }), t('Updated'));
    const remove = () => bulk((id) => api.del(`/products/${id}`), t('Deleted'));
    const exportCsv = async () => {
        try { await api.download('/products/export', `products-${siteId}.csv`); } catch (e) { toastErr(e, t('Export failed')); }
    };
    const open = (id) => navigate(sitePath(siteId, `products/${id}`));

    const columns = [
        {
            title: t('Product', { context: 'shop' }), key: 'title', render: (_, p) => (
                <Space><Thumb src={p.image?.src} /><div><a onClick={(e) => { e.stopPropagation(); open(p.id); }}>{p.title}</a>
                    {p.vendor ? <div><Text type="secondary" style={{ fontSize: 12 }}>{p.vendor}</Text></div> : null}</div></Space>
            ),
        },
        { title: t('Status'), dataIndex: 'status', width: 110, render: (v) => <StatusTag value={v} /> },
        {
            title: t('Inventory'), key: 'inv', width: 170, render: (_, p) => (p.tracked
                ? <Text type={p.inventory <= 0 ? 'danger' : undefined}>{t('{{n}} in stock', { n: p.inventory })}{p.variants_count > 1 ? ` · ${t('{{n}} variants', { n: p.variants_count })}` : ''}</Text>
                : <Text type="secondary">{t('Not tracked')}{p.variants_count > 1 ? ` · ${t('{{n}} variants', { n: p.variants_count })}` : ''}</Text>),
        },
        { title: t('Price'), key: 'price', width: 160, render: (_, p) => (p.price_min === p.price_max ? money(p.price_min, currency) : `${money(p.price_min, currency)} – ${money(p.price_max, currency)}`) },
        { title: t('Type'), dataIndex: 'product_type', width: 130, responsive: ['lg'] },
    ];

    return (
        <>
        <TranslateCatalogBanner />
        <Card
            title={t('Products', { context: 'shop' })}
            extra={(
                <Space wrap>
                    <TranslateCatalogButton />
                    <Button icon={<EditOutlined />} onClick={() => navigate(sitePath(siteId, 'products/bulk'))}>{t('Bulk edit')}</Button>
                    <Button icon={<UploadOutlined />} onClick={() => setImporting(true)}>{t('Import')}</Button>
                    <Button icon={<DownloadOutlined />} onClick={exportCsv}>{t('Export')}</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(sitePath(siteId, 'products/new'))}>{t('Add product')}</Button>
                </Space>
            )}
        >
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search allowClear placeholder={t('Search title, SKU, vendor')} value={q} onChange={(e) => setQ(e.target.value)}
                    onSearch={(v) => setFilters((f) => ({ ...f, q: v, page: 1 }))} style={{ width: 260 }} />
                <Select allowClear placeholder={t('Status')} style={{ width: 130 }} value={filters.status}
                    onChange={(v) => setFilters((f) => ({ ...f, status: v, page: 1 }))}
                    options={['active', 'draft', 'archived'].map((s) => ({ value: s, label: <StatusTag value={s} /> }))} />
                {collections.length > 0 && (
                    <Select allowClear placeholder={t('Collection')} style={{ width: 180 }} value={filters.collection_id}
                        onChange={(v) => setFilters((f) => ({ ...f, collection_id: v, page: 1 }))}
                        options={collections.map((c) => ({ value: c.id, label: c.title }))} />
                )}
                {(types.length > 0 || filters.type) && (
                    <Select allowClear placeholder={t('Type')} style={{ width: 150 }} value={filters.type}
                        onChange={(v) => setFilters((f) => ({ ...f, type: v, page: 1 }))} options={types.map((x) => ({ value: x, label: x }))} />
                )}
                {(vendors.length > 0 || filters.vendor) && (
                    <Select allowClear placeholder={t('Vendor')} style={{ width: 150 }} value={filters.vendor}
                        onChange={(v) => setFilters((f) => ({ ...f, vendor: v, page: 1 }))} options={vendors.map((x) => ({ value: x, label: x }))} />
                )}
                {sel.length > 0 && (
                    <Dropdown menu={{
                        items: [
                            { key: 'active', label: t('Set as active') },
                            { key: 'draft', label: t('Set as draft') },
                            { key: 'archived', label: t('Archive') },
                            { type: 'divider' },
                            { key: 'delete', danger: true, label: t('Delete') },
                        ],
                        onClick: ({ key }) => (key === 'delete'
                            ? Modal.confirm({ title: t('Delete {{n}} products?', { n: sel.length }), okButtonProps: { danger: true }, onOk: remove })
                            : setStatus(key)),
                    }}>
                        <Button loading={busy}>{t('{{n}} selected', { n: sel.length })} <DownOutlined /></Button>
                    </Dropdown>
                )}
            </Space>
            <Table
                rowKey="id"
                loading={loading}
                dataSource={data.items}
                columns={columns}
                scroll={{ x: 760 }}
                rowSelection={{ selectedRowKeys: sel, onChange: setSel }}
                onRow={(p) => ({ onClick: () => open(p.id), style: { cursor: 'pointer' } })}
                pagination={{
                    current: filters.page, pageSize: filters.per_page, total: data.total, showSizeChanger: true, hideOnSinglePage: data.total <= 25,
                    onChange: (page, per) => setFilters((f) => ({ ...f, page, per_page: per })),
                }}
                locale={{
                    emptyText: (
                        <Empty description={filters.q || filters.status ? t('No products match') : t('No products yet')}>
                            {!filters.q && !filters.status && <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(sitePath(siteId, 'products/new'))}>{t('Add product')}</Button>}
                        </Empty>
                    ),
                }}
            />
            <ImportModal open={importing} onClose={() => setImporting(false)} onDone={load} />
        </Card>
        </>
    );
};

export default Products;
